import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, Wrench, Shield, Camera, ChevronDown } from 'lucide-react';

// Eventbrite widget type declaration
declare global {
  interface Window {
    EBWidgets?: {
      createWidget: (config: {
        widgetType: string;
        eventId: string;
        promoCode?: string;
        modal: boolean;
        modalTriggerElementId: string;
        onOrderComplete?: () => void;
      }) => void;
    };
  }
}

// Smooth scroll helper function
const smoothScrollTo = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    const navHeight = 70; // Height of the nav bar
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - navHeight; // 20px extra padding

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

const KibuClub: React.FC = () => {
  return (
    <div className="min-h-screen bg-background pt-[104px]">
      {/* Hero Section */}
      <HeroSection />

      {/* What It Is */}
      <WhatItIsSection />

      {/* Pricing */}
      <PricingSection />

      {/* Upcoming Session */}
      <UpcomingSessionSection />

      {/* What's Included */}
      <WhatsIncludedSection />

      {/* Safety & Eligibility */}
      <SafetySection />

      {/* Media Consent */}
      <MediaConsentSection />

      {/* FAQs */}
      <FAQSection />
    </div>
  );
};

const HeroSection: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const images = [
    {
      src: "https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/boy_building.jpg",
      alt: "Kids building Kibu headphones",
      badges: {
        topLeft: "🔧 Hands-on",
        bottomRight: "🎨 Creative"
      }
    },
    {
      src: "https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/kibu-club/Hackney-Depot.webp",
      alt: "Hackney Depot Space",
      badges: {
        topLeft: "📍 Hackney Depot",
        bottomRight: "✨ Modern space"
      }
    },
    {
      src: "https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/girl_and_father_building.jpg",
      alt: "Girl and father building Kibu headphones",
      badges: {
        topLeft: "👨‍👧 Perfect for Kids",
        bottomRight: "🤝 Learn together"
      }
    },
    {
      src: "https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/girl_building.jpg",
      alt: "Girl building Kibu headphones",
      badges: {
        topLeft: "🎧 Build yours",
        bottomRight: "♻️ Sustainable"
      }
    },
    {
      src: "https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/parents_and_son_building.jpg",
      alt: "Parents and son building Kibu headphones",
      badges: {
        topLeft: "👨‍👩‍👦 Family fun",
        bottomRight: "💡 Learn skills"
      }
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
        setIsAnimating(false);
      }, 600);
    }, 4000);

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section className="relative bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:py-20 md:py-10 md:my-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="order-2 md:order-1">
            <div className="inline-block bg-yellowMedium/10 text-yellowMedium px-4 py-2 rounded-full text-sm font-headline mb-6">
              🎉 October Half-Term Special
            </div>

            <h1 className="text-4xl md:text-6xl font-headline mb-6 leading-tight">
              Welcome to<br />
              <span className="text-yellowMedium">Kibu Club</span>
            </h1>

            <p className="text-xl md:text-2xl mb-6 text-gray-700">
              Build your own headphones. Learn how things work. Keep what you make.
            </p>

            <p className="text-lg mb-8 text-gray-600 leading-relaxed">
              A hands-on workshop where kids explore 3D printing, repair, and recycling while creating something they'll treasure forever.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button
                onClick={() => smoothScrollTo('register')}
                className="inline-block bg-yellowMedium text-white px-8 py-4 flex items-center justify-center rounded-large text-lg font-headline hover:shadow-button-hover transition-all duration-300 transform hover:-translate-y-1 text-center cursor-pointer"
              >
                Book Your Spot →
              </button>
              <button
                onClick={() => smoothScrollTo('what-it-is')}
                className="inline-block bg-white text-gray-800 px-8 py-4 flex items-center justify-center rounded-large text-lg font-headline border-2 border-grey hover:border-yellowMedium transition-all duration-300 text-center cursor-pointer"
              >
                Learn More
              </button>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-grey">
              <div>
                <div className="text-2xl font-headline text-yellowMedium mb-1 md:text-start text-center">1h</div>
                <div className="text-sm text-gray-600 md:text-start text-center">Duration</div>
              </div>
              <div>
                <div className="text-2xl font-headline text-yellowMedium mb-1 md:text-start text-center">Ages 5+</div>
                <div className="text-sm text-gray-600 md:text-start text-center">All welcome</div>
              </div>
              <div>
                <div className="text-2xl font-headline text-yellowMedium mb-1 md:text-start text-center">£30*</div>
                <div className="text-sm text-gray-600 md:text-start text-center">Early bird</div>
              </div>
            </div>
          </div>

          {/* Right: Stacked Image Slideshow */}
          <div className="order-1 md:order-2">
            <div className="relative aspect-square">
              {/* Stack of images */}
              <div className="relative w-full h-full" style={{ perspective: '1000px' }}>
                {images.map((image, index) => {
                  const position = (index - currentImageIndex + images.length) % images.length;
                  const isTop = position === 0;

                  return (
                    <div
                      key={index}
                      className={`absolute inset-0 rounded-large md:rounded-huge overflow-hidden shadow-lg transition-all duration-600 ease-out ${isTop && isAnimating ? 'animate-card-exit' : ''
                        }`}
                      style={{
                        transform: `
                          translateY(${position * -8}px)
                          translateX(${position * 4}px)
                          scale(${1 - position * 0.05})
                          rotateY(${position * -2}deg)
                        `,
                        zIndex: images.length - position,
                        opacity: position > 2 ? 0 : 1 - position * 0.2,
                        pointerEvents: position === 0 ? 'auto' : 'none'
                      }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />

                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

                      {/* Floating badges - only show on top card */}
                      {isTop && !isAnimating && image.badges && (
                        <>
                          <div className="absolute top-6 left-6 bg-white rounded-full px-4 py-2 shadow-lg animate-fade-in">
                            <span className="font-headline text-sm">{image.badges.topLeft}</span>
                          </div>
                          <div className="absolute bottom-6 right-6 bg-white rounded-full px-4 py-2 shadow-lg animate-fade-in">
                            <span className="font-headline text-sm">{image.badges.bottomRight}</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Small accent badge */}
              <div className="absolute -bottom-6 -left-6 bg-yellowMedium text-black p-6 rounded-xl md:rounded-large shadow-lg transform rotate-3 z-20">
                <div className="font-headline text-lg -mb-1">Limited to</div>
                <div className="font-headline text-3xl mb-1">20 spots!</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhatItIsSection: React.FC = () => {
  const features = [
    { icon: Users, text: "Ages 5+" },
    { icon: Clock, text: "1 hour 15 minutes of hands-on fun" },
    { icon: Wrench, text: "Learn 3D printing, assembly, and repair skills" },
    { icon: Camera, text: "Keep your own custom Kibu headphones!" }
  ];

  return (
    <section id="what-it-is" className="py-10 pt-20 px-6">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-center mb-12 font-headline">What is Kibu Club?</h2>

        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, idx) => (
            <div key={idx} className="flex items-start gap-4 bg-white p-6 rounded-large shadow-sm">
              <div className="bg-yellowMedium/10 p-3 rounded-hero">
                <feature.icon className="w-6 h-6 text-yellowMedium" />
              </div>
              <p className="text-lg flex-1 pt-2">{feature.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white p-8 rounded-large shadow-sm">
          <h3 className="mb-4 font-headline">What Kids Do & Learn</h3>
          <ul className="space-y-3 text-lg">
            <li className="flex items-start gap-3">
              <span className="text-yellowMedium mt-1">•</span>
              <span>Discover how 3D printing works and why it matters for sustainability</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-yellowMedium mt-1">•</span>
              <span>Assemble their own Kibu headphones step-by-step with guidance</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-yellowMedium mt-1">•</span>
              <span>Learn basic repair skills to fix things instead of throwing them away</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-yellowMedium mt-1">•</span>
              <span>Take home working headphones they built themselves!</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

const UpcomingSessionSection: React.FC = () => {
  // TEST MODE: Set to true to enable test controls, false for production
  const TEST_MODE = false;

  // Kibu Club workshop date - October 28th, 2025 at 3:00 PM BST (GMT+1)
  // Note: In October 2025, UK will be in BST until October 26th, then GMT
  // October 28th is after BST ends, so this is GMT (no offset)
  const eventDate = new Date('2025-10-28T15:00:00+00:00'); // 3pm GMT (UK standard time)
  const eventEndDate = new Date('2025-10-28T14:15:00+00:00'); // 4:15pm GMT (1h 15min workshop)
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(eventDate));
  const [isEventOver, setIsEventOver] = useState(false);
  const [isEventInProgress, setIsEventInProgress] = useState(false);

  // Test mode controls
  const [testState, setTestState] = useState<'normal' | 'inProgress' | 'over'>('normal');

  // Load Eventbrite widget script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.eventbrite.com/static/widgets/eb_widgets.js';
    script.async = true;

    script.onload = () => {
      // Initialize widget after script loads
      if (window.EBWidgets) {
        window.EBWidgets.createWidget({
          widgetType: 'checkout',
          eventId: '1781393639329',
          promoCode: 'Super-Early-Bird',
          modal: true,
          modalTriggerElementId: 'eventbrite-widget-modal-trigger-1781393639329',
          onOrderComplete: () => {
            console.log('Order complete!');
          }
        });
      }
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const timeUntilStart = calculateTimeLeft(eventDate);

      // Always check actual time to see if we should auto-transition
      const actuallyInProgress = now >= eventDate && now < eventEndDate;
      const actuallyOver = now >= eventEndDate;

      // If in test mode, use manual test state UNLESS the real event has started/ended
      if (TEST_MODE) {
        // Auto-transition if the real event has started or ended
        if (actuallyInProgress) {
          setIsEventInProgress(true);
          setIsEventOver(false);
          setTestState('inProgress'); // Update test state to match reality
        } else if (actuallyOver) {
          setIsEventOver(true);
          setIsEventInProgress(false);
          setTestState('over'); // Update test state to match reality
        } else {
          // Use manual test controls only when event hasn't actually started
          if (testState === 'inProgress') {
            setIsEventInProgress(true);
            setIsEventOver(false);
          } else if (testState === 'over') {
            setIsEventOver(true);
            setIsEventInProgress(false);
          } else {
            setIsEventInProgress(false);
            setIsEventOver(false);
            setTimeLeft(timeUntilStart);
          }
        }
        return;
      }

      // Normal production mode
      if (actuallyInProgress) {
        setIsEventInProgress(true);
        setIsEventOver(false);
      } else if (actuallyOver) {
        setIsEventOver(true);
        setIsEventInProgress(false);
      } else {
        setIsEventInProgress(false);
        setIsEventOver(false);
        setTimeLeft(timeUntilStart);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [eventDate, eventEndDate, TEST_MODE, testState]);

  return (
    <section className="pb-20 pt-20 px-6 bg-white" id="register">
      <div className="max-w-5xl mx-auto">
        {/* TEST MODE CONTROLS */}
        {TEST_MODE && (
          <div className="mb-8 p-4 bg-gray-100 rounded-large border-2 border-gray-300">
            <p className="text-sm font-headline mb-3 text-center text-gray-700">
              🧪 TEST MODE - Choose a state to preview:
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => setTestState('normal')}
                className={`px-4 py-2 rounded-large font-headline text-sm transition-all ${testState === 'normal'
                  ? 'bg-yellowMedium text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
                  }`}
              >
                Before Workshop
              </button>
              <button
                onClick={() => setTestState('inProgress')}
                className={`px-4 py-2 rounded-large font-headline text-sm transition-all ${testState === 'inProgress'
                  ? 'bg-green text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
                  }`}
              >
                During Workshop
              </button>
              <button
                onClick={() => setTestState('over')}
                className={`px-4 py-2 rounded-large font-headline text-sm transition-all ${testState === 'over'
                  ? 'bg-gray-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
                  }`}
              >
                After Workshop
              </button>
            </div>
          </div>
        )}

        <h2 className="text-center mb-12 font-headline">
          {isEventOver ? 'Past Session' : isEventInProgress ? 'Workshop In Progress!' : 'Upcoming Session'}
        </h2>

        {/* Countdown Timer or Event Status */}
        {isEventOver ? (
          <div className="mb-12 bg-gradient-to-br from-gray-500 to-gray-600 p-8 rounded-large text-white text-center">
            <h3 className="text-2xl font-headline mb-4">🎉 Workshop Complete!</h3>
            <p className="text-lg opacity-90 mb-6">
              Thank you to everyone who joined us for this session!
            </p>
            <p className="text-base opacity-80">
              Interested in future workshops? Get in touch at club@kibu.family
            </p>
          </div>
        ) : isEventInProgress ? (
          <div className="mb-12 bg-gradient-to-br from-green to-green-600 p-8 rounded-large text-white text-center">
            <h3 className="text-2xl font-headline mb-4">🔧 Workshop is happening now!</h3>
            <p className="text-lg opacity-90 mb-4">
              Our participants are building their Kibu headphones right now
            </p>
            <p className="text-base opacity-80">
              Running late? Contact us at club@kibu.family
            </p>
          </div>
        ) : (
          <div className="mb-12 bg-gradient-to-br from-yellowMedium to-primary p-8 rounded-large text-white">
            <p className="text-center text-lg mb-4 opacity-90">Workshop starts in:</p>
            <div className="grid grid-cols-4 gap-4 max-w-2xl mx-auto">
              {Object.entries(timeLeft).map(([unit, value]) => (
                <div key={unit} className="text-center">
                  <div className="bg-white/20 backdrop-blur-sm rounded-hero p-4 mb-2 flex items-center justify-center">
                    <span className="text-4xl font-headline">{value}</span>
                  </div>
                  <span className="text-sm uppercase tracking-wide opacity-80">{unit}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Session Details */}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <Calendar className="w-6 h-6 text-yellowMedium mt-1" />
              <div>
                <h4 className="font-headline mb-1">Date</h4>
                <p className="text-lg">Tuesday, October 28th, 2025</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Clock className="w-6 h-6 text-yellowMedium mt-1" />
              <div>
                <h4 className="font-headline mb-1">Time</h4>
                <p className="text-lg">3:00 PM - 4:15 PM</p>
                <p className="text-sm text-gray-600 mt-1">Check-in from 2:45 PM</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-yellowMedium mt-1" />
              <div>
                <h4 className="font-headline mb-1">Location</h4>
                <p className="text-lg">Hackney Depot</p>
                <p className="text-sm text-gray-600 mt-1">5 Sheep Ln, London E8 4QS</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Users className="w-6 h-6 text-yellowMedium mt-1" />
              <div>
                <h4 className="font-headline mb-1">Capacity</h4>
                <p className="text-lg">Limited to 20 participants</p>
                <div className="mt-2">
                  {/* <div className="bg-grey h-2 rounded-full overflow-hidden">
                    <div className="bg-yellowMedium h-full w-[40%]"></div>
                  </div> */}
                  {/* <p className="text-sm text-gray-600 mt-1">5 of 20 spots remaining</p> */}
                </div>
              </div>
            </div>
          </div>

          {/* Google Map */}
          <div className="bg-grey rounded-large overflow-hidden h-[400px]">
            <iframe
              title="Hackney Depot Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2481.4834821668844!2d-0.0609887!3d51.542819299999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761cb1c8d6e7e7%3A0x8c3f8e3b8e3b8e3b!2s5%20Sheep%20Ln%2C%20London%20E8%204QS!5e0!3m2!1sen!2suk!4v1234567890123"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-12 text-center">
          {/* Noscript fallback for SEO */}
          <noscript>
            <a
              href="https://www.eventbrite.co.uk/e/kibu-club-build-your-own-headphones-workshop-hackney-tickets-1781393639329"
              rel="noopener noreferrer"
              target="_blank"
              className="inline-block bg-yellowMedium text-white px-10 py-4 rounded-large text-lg font-headline hover:shadow-button-hover transition-all duration-300 transform hover:-translate-y-1"
            >
              Buy Tickets on Eventbrite
            </a>
          </noscript>

          {/* Eventbrite widget trigger button */}
          <button
            id="eventbrite-widget-modal-trigger-1781393639329"
            type="button"
            className="inline-block bg-yellowMedium text-white px-10 py-4 rounded-large text-lg font-headline hover:shadow-button-hover transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
          >
            Reserve Your Spot on Eventbrite
          </button>

          <p className="mt-4 text-sm text-gray-600">
            Secure checkout powered by Eventbrite
          </p>
        </div>
      </div>
    </section>
  );
};

const PricingSection: React.FC = () => {
  const isEarlyBirdActive = true; // Toggle this for early bird pricing
  const earlyBirdDeadline = "October 14th";

  return (
    <section className="py-10 px-6 bg-background">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-center mb-12 font-headline">Pricing</h2>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Early Bird */}
          {isEarlyBirdActive && (
            <div className="bg-primary p-8 py-11 md:p-8 rounded-large shadow-lg text-white relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-white text-yellowMedium px-3 py-1 rounded-full text-sm font-headline">
                Save £10!
              </div>
              <p className="flex items-end mb-4">
                <h3 className="font-headline mr-2">Special Launch Price</h3>
                <span className="text-xs opacity-90 mb-1">til {earlyBirdDeadline}</span>
              </p>
              <div className="mb-6">
                <span className="text-4xl font-headline">£30</span>
                <span className="opacity-90 ml-2">per child</span>
              </div>
              {/* <p className="mb-6 opacity-90">
                Book before {earlyBirdDeadline} and save!
              </p> */}
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-hero mb-6">
                <p className="text-sm font-headline">
                  🔥 Amazing deal: Get headphones + workshop for just £30!
                </p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Everything in standard price</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Priority access to future workshops</span>
                </li>
              </ul>
            </div>
          )}
          {/* Standard Price */}
          <div className="bg-white p-8 py-11 md:p-8 rounded-large shadow-sm">
            <h3 className="font-headline mb-4">Standard Price</h3>
            <div className="mb-6">
              <span className="text-4xl font-headline">£40</span>
              <span className="text-gray-600 ml-2">per child</span>
            </div>
            <div className="bg-yellowMedium/10 p-4 rounded-hero mb-6">
              <p className="text-sm font-headline text-yellowMedium">
                🎧 Includes Kibu headphones worth £39+ retail - you're getting the workshop experience for FREE!
              </p>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <span className="text-yellowMedium">✓</span>
                <span>Full workshop access</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellowMedium">✓</span>
                <span>Complete Kibu headphone kit (£39+ value!)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellowMedium">✓</span>
                <span>All materials & tools provided</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellowMedium">✓</span>
                <span>Expert guidance throughout</span>
              </li>
            </ul>
          </div>
        </div>

        {/* <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Group discounts available for 4+ children. Contact us for details.
          </p>
        </div> */}
      </div>
    </section>
  );
};

const WhatsIncludedSection: React.FC = () => {
  return (
    <section className="py-10 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-center mb-12 font-headline">What's Included</h2>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-background p-6 rounded-large">
            <h3 className="font-headline mb-4 text-green">Included in Your Workshop</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green">✓</span>
                <span>Complete Kibu headphone kit (yours to keep!)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green">✓</span>
                <span>All assembly tools and materials</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green">✓</span>
                <span>Expert guidance from our facilitators</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green">✓</span>
                <span>Printed take-home guide for repairs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green">✓</span>
                <span>Light refreshments (water & snacks)</span>
              </li>
            </ul>
          </div>

          <div className="bg-background p-6 rounded-large">
            <h3 className="font-headline mb-4 text-yellowMedium">What to Bring</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-yellowMedium">•</span>
                <span>Curiosity and excitement to learn!</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellowMedium">•</span>
                <span>Comfortable clothing (may get a bit messy)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellowMedium">•</span>
                <span>Parent/guardian for children under 8</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellowMedium">•</span>
                <span>Device to test headphones (optional)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const SafetySection: React.FC = () => {
  return (
    <section className="py-10 px-6 bg-background">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-12">
          <Shield className="w-8 h-8 text-green" />
          <h2 className="font-headline">Safety & Eligibility</h2>
        </div>

        <div className="bg-white p-8 rounded-large shadow-sm space-y-6">
          <div>
            <h3 className="font-headline mb-3">Age Guidance</h3>
            <p className="text-lg">
              Recommended for children aged 5 and above. A parent/guardian is required to stay for any child aged 5-8. For ages 9+, drop-off is allowed but parent/guardian must remain contactable. Younger kids are welcome to join you, however we are unable to allow them to participate in the workshop.
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">Safety Measures</h3>
            <ul className="space-y-2 text-lg">
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>DBS-checked staff and volunteers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>Secure check-in with wristbands/tokens</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>Mandatory parent/guardian contact information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>Controlled, supervised activities</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>Clear no-nut/snack guidance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>On-site first aid and incident procedures</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>Venue risk assessment completed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green">✓</span>
                <span>Photo consent managed</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-headline mb-3">Drop-off & Pick-up Policy</h3>
            <p className="text-lg mb-3">
              <strong>Drop-off:</strong> Arrive from 2:45 pm for check-in. We start promptly at 3:00 pm.
            </p>
            <p className="text-lg mb-3">
              <strong>Pick-up:</strong> Collection is at 4:15 pm. The same adult who drops off must pick up. A late pickup fee may apply after a 10-minute grace period.
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">Accessibility</h3>
            <p className="text-lg">
              We strive to make Kibu Club accessible to all. The venue is wheelchair accessible and we can accommodate various learning needs. Please contact us in advance to discuss specific requirements.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const MediaConsentSection: React.FC = () => {
  return (
    <section className="py-10 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-12">
          <Camera className="w-8 h-8 text-yellowMedium" />
          <h2 className="font-headline">Filming & Photography Notice</h2>
        </div>

        {/* Important Notice Banner */}
        <div className="bg-blue-50 border-2 border-blue-300 p-6 rounded-large mb-8">
          <div className="flex items-start gap-3">
            <span className="text-blue-600 text-2xl">📹</span>
            <div>
              <h3 className="font-headline text-blue-900 mb-2">This is a Filmed Event</h3>
              <p className="text-blue-900 font-semibold mb-3">
                We will capture still images, video and audio during the workshop. A third-party news crew may also film for journalistic purposes.
              </p>
              <p className="text-blue-900">
                <strong>By attending, you acknowledge that filming will occur.</strong> Consent for Kibu to use footage in marketing materials will be collected via separate, unticked checkboxes at checkout.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background p-8 rounded-large space-y-6">
          <div>
            <h3 className="font-headline mb-3">Kibu Marketing Use (Optional)</h3>
            <p className="text-gray-700 mb-3">
              If you give consent at checkout, you grant Kibu a non-exclusive, royalty-free, worldwide, perpetual licence to use, edit and publish images/recordings of you and/or your child for Kibu marketing and promotional purposes (including websites, social media, PR, and advertising).
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">If You Do Not Consent to Marketing Use</h3>
            <p className="text-gray-700 mb-3">
              Our team will do what is reasonably possible to avoid filming/photographing you and your child. However, <strong>given the nature of the event, we cannot guarantee complete avoidance</strong>.
            </p>
            <p className="text-gray-700">
              If you prefer not to be filmed or photographed at all, please do not purchase a ticket. Contact us at <a href="mailto:contact@kibu.family" className="text-yellowMedium underline">contact@kibu.family</a> for information about future non-filmed sessions.
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">Third-Party Media Filming</h3>
            <p className="text-gray-700">
              Any news organisation filming at the event acts independently under journalistic rules as a separate controller. Kibu does not control their use. Queries about their footage should be directed to them.
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">Children & Parental Responsibility</h3>
            <p className="text-gray-700">
              For attendees under 18, consent must be given by a parent/legal guardian with parental responsibility. By providing consent at checkout, you confirm you are that person.
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">Withdrawing Consent</h3>
            <p className="text-gray-700">
              You may withdraw consent at any time by emailing <a href="mailto:privacy@kibu.family" className="text-yellowMedium underline">privacy@kibu.family</a>. We will stop future use as soon as reasonably possible. This may not affect materials already printed, distributed, or shared by third parties.
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">Safeguarding</h3>
            <p className="text-gray-700">
              We avoid publishing images with full names or personal details of children. Safeguarding concerns should be directed to <a href="mailto:safeguarding@kibu.family" className="text-yellowMedium underline">safeguarding@kibu.family</a>.
            </p>
          </div>

          <div>
            <h3 className="font-headline mb-3">Refund Option</h3>
            <p className="text-gray-700">
              If you purchased a ticket but do not wish to be filmed/photographed, contact us within 7 days of purchase and no later than 72 hours before the event to discuss options or a refund.
            </p>
          </div>

          <div className="bg-white p-4 rounded-hero border-2 border-yellowMedium">
            <p className="text-sm">
              <strong>Data Controller:</strong> Kibu Limited (trading as "Kibu")<br />
              <strong>Contact:</strong> <a href="mailto:privacy@kibu.family" className="text-yellowMedium underline">privacy@kibu.family</a>, 1a Dunn Street, E8 2DG<br />
              <strong>Full Terms:</strong> Please read the complete Additional Terms & Conditions during checkout.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQSection: React.FC = () => {
  const faqs = [
    {
      question: "Can I leave my child?",
      answer: "A parent/guardian is required to stay for any child aged 5-8. For ages 9+, drop-off is allowed, but a parent/guardian must remain contactable. Younger kids are more than welcome to join you; however, we are unable to allow them to participate in the workshop."
    },
    {
      question: "What's included?",
      answer: "Kibu headphone kit to build and take home; all tools and materials provided."
    },
    {
      question: "What's the format?",
      answer: "Station-based activities; 3D printing demo; guided build and test of their own pair of headphones."
    },
    {
      question: "What's the capacity?",
      answer: "Limited to 20 spots to ensure a safe child-to-adult ratio."
    },
    {
      question: "What do I need to know about drop off?",
      answer: "Drop-off: Arrive from 2:45 pm for check-in. We start promptly at 3:00 pm. A parent/guardian must stay on-site for children aged 8 and under. For ages 9+, drop-off is allowed."
    },
    {
      question: "What do I need to know about pick up?",
      answer: "Pickup: Collection is at 4:15 pm. The same adult who drops off must pick up. A late pickup fee may apply after a 10-minute grace period."
    },
    {
      question: "Who will be leading the workshop?",
      answer: "The workshop will be led by experienced, DBS-checked staff and volunteers."
    },
    {
      question: "What to bring/wear?",
      answer: "Comfortable clothes, closed-toe shoes; water bottle optional; no nut products."
    },
    {
      question: "What is the photo/filming policy?",
      answer: "This is a filmed event - we will capture images, video and audio, and third-party news crews may also film. By attending, you acknowledge filming will occur. Consent for Kibu to use footage in marketing is optional via unticked checkboxes at checkout. If you don't consent to marketing use, we'll try to avoid filming you, but cannot guarantee complete avoidance. If you prefer not to be filmed at all, please contact us before purchasing."
    },
    {
      question: "What safety measures are in place?",
      answer: "We run DBS-checked staff; secure check-in with wristbands/tokens; mandatory parent/guardian contacts; controlled, supervised activities; clear no-nut/snack guidance; on-site first aid and incident procedures; venue risk assessment completed; photo consent managed; optional parent stay nearby."
    },
    {
      question: "How can I contact you with more questions?",
      answer: "Email us at club@kibu.family. We typically respond within 24 hours and are happy to answer any questions!"
    }
  ];

  return (
    <section className="py-10 px-6 bg-background">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-center mb-12 font-headline">Frequently Asked Questions</h2>

        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <FAQItem key={idx} question={faq.question} answer={faq.answer} />
          ))}
        </div>

        <div className="mt-12 text-center bg-white p-8 rounded-large">
          <h3 className="font-headline mb-4">Still have questions?</h3>
          <p className="mb-6 text-lg">We're here to help! Get in touch and we'll get back to you promptly.</p>
          <a
            href="mailto:club@kibu.family"
            className="inline-block bg-yellowMedium text-white px-8 py-3 rounded-large font-headline hover:shadow-button-hover transition-all duration-300"
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
};

const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-large overflow-hidden shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-background/50 transition-colors duration-200"
      >
        <h4 className="font-headline pr-4">{question}</h4>
        <ChevronDown
          className={`w-5 h-5 text-yellowMedium transition-transform duration-200 flex-shrink-0 ${isOpen ? 'transform rotate-180' : ''
            }`}
        />
      </button>

      {isOpen && (
        <div className="px-6 pb-4 pt-2">
          <p className="text-gray-700 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

// Helper function for countdown timer
function calculateTimeLeft(eventDate: Date) {
  const difference = +eventDate - +new Date();

  if (difference > 0) {
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }

  return { days: 0, hours: 0, minutes: 0, seconds: 0 };
}

export default KibuClub;
